import { ChangeEvent, useEffect, useState, useRef } from "react";
import { Card, Col, Row, Modal, Form } from "react-bootstrap";
import { Permission } from "permissions/AdminRolePermissionGate";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../redux/HooksRedux";
import StandardButton from "shared/StandardButton/StandardButton";

import * as Subscriptions from "../../../redux/subscriptions/Subscriptions";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { LoadingAnimation } from "shared/Loader/Loader";
import AdvanceTable from "components/base/AdvanceTable";
import CustomAdvanceTableFooter from "shared/StandardTable/CustomAdvanceTableFooter";
import useAdvanceTable from "hooks/useAdvanceTable";
import { ColumnDef } from "@tanstack/react-table";
import { AppDispatch } from "redux/store";
import { formatDate } from "utils/helpers";
import SearchBox from "components/common/SearchBox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faPlus,
  faUser,
  faBuilding,
  faPhone,
  faLocationDot,
  faEnvelope,
  faCalendarDays,
  faClock,
  faIndianRupeeSign,
  faHourglassHalf,
  faXmark,
  faFilter,
  faTimes,
  faSearch,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import Button from "components/base/Button";
import { config } from "config";
import Select from "react-select";
import "../../../shared/FilterPanel/filterPanel.scss";
import "./subscriptionDetailsModal.scss";

// ============= Updated Interfaces =============

interface ISubscription {
  owner_subscription_id: number;
  user_id: number;
  name: string;
  mobile_number: string;
  email: string;
  role_name: string;
  plan_id: number;
  plan_name: string;
  plan_count: number;
  used_count: number;
  start_date: string;
  end_date: string;
  amount: number;
  status: string;
  is_active: boolean;
  created_at: string;
  payment_master_id: number;
  payment_status: string;
}

interface ISubscriptionDetail {
  owner_subscription_id: number;
  user_id: number;
  name: string;
  mobile_number: string;
  email: string;
  role_name: string;
  plan_id: number;
  plan_name: string;
  plan_count: number;
  used_count: number;
  start_date: string;
  end_date: string;
  amount: number;
  status: string;
  is_active: boolean;
  created_at: string;
  payment_master_id: number;
  payment_status: string;
  company_name?: string;
  company_address?: string;
  company_description?: string;
  gst_number?: string;
  updated_at?: string;
  is_customized?: boolean;
}

// ============= Main Component =============

const UserSubscription = () => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState<ISubscription[]>([]);
  const dispatch: AppDispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  
  // ============= Filter States =============
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>("");
  const [postedFromDate, setPostedFromDate] = useState<string>("");
  const [postedToDate, setPostedToDate] = useState<string>("");
  
  const [isAllVisibleData, setIsAllVisibleData] = useState<boolean>(false);
  const [showViewModal, setShowViewModal] = useState<boolean>(false);
  const [selectedSubscription, setSelectedSubscription] = useState<ISubscriptionDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);

  const isInitialMount = useRef(true);
  const limit = config.pageLimit || 10;

  // ============= Build Filter Params =============
  const buildFilterParams = (pageNo: number, recordPerPage: number) => {
    const params: any = {
      search_text: debouncedSearchText || "",
      page_no: pageNo,
      record_per_page: recordPerPage,
    };

    if (postedFromDate) params.posted_from_date = postedFromDate;
    if (postedToDate) params.posted_to_date = postedToDate;

    return params;
  };

  // ============= API Calls with Filters =============

  const getTotalSubscriptionCount = async () => {
    try {
      const params = buildFilterParams(1, limit);
      const response: any = await dispatch(
        Subscriptions.owner_subscriptions_get_all_count(params)
      );

      let count = 0;
      if (response?.data?.data?.[0]?.total_owner_subscription_count) {
        count = response.data.data[0].total_owner_subscription_count;
      } else if (response?.data?.data?.total_owner_subscription_count) {
        count = response.data.data.total_owner_subscription_count;
      } else if (response?.data?.total_owner_subscription_count) {
        count = response.data.total_owner_subscription_count;
      } else if (response?.payload?.data?.[0]?.total_owner_subscription_count) {
        count = response.payload.data[0].total_owner_subscription_count;
      }

      setTotalRecords(count);
      setTotalPages(Math.ceil(count / limit));
    } catch (err) {
      console.error("Error fetching subscription count:", err);
      setTotalRecords(0);
      setTotalPages(0);
    }
  };

  const getAllSubscriptions = async () => {
    setIsLoading(true);
    try {
      const pageNo = isAllVisibleData ? 1 : currentPage;
      const recordPerPage = isAllVisibleData ? totalRecords : limit;
      const params = buildFilterParams(pageNo, recordPerPage);

      const response: any = await dispatch(
        Subscriptions.owner_subscriptions_get_all(params)
      );

      let data = [];
      if (response?.data?.data) {
        data = response.data.data;
      } else if (Array.isArray(response?.data)) {
        data = response.data;
      } else if (response?.payload?.data) {
        data = response.payload.data;
      } else if (Array.isArray(response?.payload)) {
        data = response.payload;
      }

      setTableData(data);
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getSubscriptionById = async (owner_subscription_id: number) => {
    setIsLoadingDetail(true);
    try {
      const params = {
        owner_subscription_id: owner_subscription_id,
      };

      const response: any = await dispatch(
        Subscriptions.user_subscriptions_get_by_user_subscription_id(params)
      );

      let subscriptionData = null;
      if (response?.data?.data?.[0]) {
        subscriptionData = response.data.data[0];
      } else if (response?.data) {
        subscriptionData = response.data;
      } else if (response?.payload?.data) {
        subscriptionData = response.payload.data;
      }

      setSelectedSubscription(subscriptionData);
      setShowViewModal(true);
    } catch (err) {
      console.error("Error fetching subscription details:", err);
      setSelectedSubscription(null);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  // ============= Event Handlers =============

  const handleViewClick = (subscription: ISubscription) => {
    getSubscriptionById(subscription.owner_subscription_id);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedSubscription(null);
  };

  const fetchSubscriptionsData = async () => {
    await getTotalSubscriptionCount();
    await getAllSubscriptions();
  };

  // ============= Search and Filter Handlers =============

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsAllVisibleData(false);
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handleFromDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPostedFromDate(e.target.value);
    setCurrentPage(1);
  };

  const handleToDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPostedToDate(e.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchText("");
    setDebouncedSearchText("");
    setPostedFromDate("");
    setPostedToDate("");
    setCurrentPage(1);
  };

  const getCanPreviousPage = () => currentPage > 1;
  const getCanNextPage = () => totalPages > currentPage;

  const handleNext = () => {
    const pageNumber = currentPage + 1;
    setCurrentPage(pageNumber);
  };

  const handlePrevious = () => {
    const pageNumber = currentPage - 1;
    setCurrentPage(pageNumber);
  };

  // ============= useEffect Hooks =============

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchSubscriptionsData();
    } else {
      fetchSubscriptionsData();
    }
  }, [currentPage, debouncedSearchText, postedFromDate, postedToDate]);

  // ============= Table Columns =============

  const subscriptionTableColumns: ColumnDef<ISubscription>[] = [
    {
      accessorKey: "owner_subscription_id",
      header: "ID",
      cell: ({ row: { original } }) => (
        <p className="mb-0 text-body-emphasis fw-semibold">
          {original.owner_subscription_id}
        </p>
      ),
    },
    {
      accessorKey: "plan_name",
      header: "Plan Name",
      cell: ({ row: { original } }) => (
        <p className="mb-0 text-body-emphasis fw-semibold">
          {original.plan_name}
        </p>
      ),
    },
    {
      accessorKey: "name",
      header: "User Name",
      cell: ({ row: { original } }) => (
        <p className="mb-0 text-body-emphasis fw-semibold">{original.name}</p>
      ),
    },
    {
      accessorKey: "mobile_number",
      header: "Mobile Number",
      cell: ({ row: { original } }) => (
        <p className="mb-0 text-body-emphasis fw-semibold">
          {original.mobile_number}
        </p>
      ),
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row: { original } }) => (
        <p className="mb-0 text-body-emphasis fw-semibold">
          {original.email || "N/A"}
        </p>
      ),
    },
    {
      accessorKey: "role_name",
      header: "Role",
      cell: ({ row: { original } }) => (
        <p className="mb-0 text-body-emphasis fw-semibold">
          {original.role_name || "N/A"}
        </p>
      ),
    },
    {
      accessorKey: "plan_count",
      header: "Plan Count",
      cell: ({ row: { original } }) => (
        <p className="mb-0 text-body-emphasis fw-semibold">
          {original.plan_count || 0}
        </p>
      ),
    },
    {
      accessorKey: "used_count",
      header: "Used Count",
      cell: ({ row: { original } }) => (
        <p className="mb-0 text-body-emphasis fw-semibold">
          {original.used_count || 0}
        </p>
      ),
    },
    {
      accessorKey: "start_date",
      header: "Start Date",
      cell: ({ row: { original } }) => (
        <p className="mb-0 text-body-emphasis fw-semibold">
          {original.start_date ? formatDate(original.start_date.toString()) : "N/A"}
        </p>
      ),
    },
    {
      accessorKey: "end_date",
      header: "End Date",
      cell: ({ row: { original } }) => (
        <p className="mb-0 text-body-emphasis fw-semibold">
          {original.end_date ? formatDate(original.end_date.toString()) : "N/A"}
        </p>
      ),
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: ({ row: { original } }) => (
        <p className="mb-0 text-body-emphasis fw-semibold">
          ₹{Number(original.amount).toLocaleString()}
        </p>
      ),
    },
    {
      accessorKey: "payment_status",
      header: "Payment Status",
      cell: ({ row: { original } }) => {
        const paymentColor =
          original.payment_status === "SUCCESS" ? "warning" :
          original.payment_status === "FAILED" ? "danger" :
          original.payment_status === "failed" ? "danger" :
          "warning";

        return (
          <span className={`badge bg-${paymentColor}`}>
            {original.payment_status || "N/A"}
          </span>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      cell: ({ row: { original } }) => (
        <p className="mb-0 text-body-emphasis fw-semibold">
          {formatDate(original.created_at.toString())}
        </p>
      ),
    },
    // {
    //   accessorKey: "action",
    //   header: "Action",
    //   cell: ({ row: { original } }) => (
    //     <div className="ab-row-actions">
    //       <Permission pageName="Subscription" actionKey="view">
    //         <Button
    //           variant=""
    //           onClick={() => handleViewClick(original)}
    //           className="border-0 ab-row-action-btn ab-row-action-btn--view"
    //           title="View Details"
    //         >
    //           <FontAwesomeIcon icon={faEye} />
    //         </Button>
    //       </Permission>
    //     </div>
    //   ),
    // },
  ];

  const table = useAdvanceTable({
    data: tableData,
    columns: subscriptionTableColumns,
    pageSize: limit,
    pagination: true,
    sortable: false,
    selection: false,
  });

  const safeFormatDate = (dateValue: any) => {
    if (!dateValue) return "N/A";
    try {
      return formatDate(dateValue.toString());
    } catch (error) {
      return "Invalid Date";
    }
  };

  // ============= View Modal Helpers =============

  const getPlanTier = (planName?: string) => {
    const name = (planName || "").toLowerCase();
    if (name.includes("diamond")) return "diamond";
    if (name.includes("platinum")) return "platinum";
    if (name.includes("gold")) return "gold";
    if (name.includes("silver")) return "silver";
    return "silver";
  };

  const getStatusKey = (status?: string) => {
    const key = (status || "").toLowerCase();
    return ["active", "expired", "pending", "cancelled", "inactive"].includes(key) ? key : "cancelled";
  };

  const formatSubscriptionId = (id?: number) =>
    id || id === 0 ? `#${String(id).padStart(5, "0")}` : "N/A";

  const daysBetween = (from?: string, to?: string) => {
    if (!from || !to) return null;
    const a = new Date(from);
    const b = new Date(to);
    if (isNaN(a.getTime()) || isNaN(b.getTime())) return null;
    return Math.round((b.getTime() - a.getTime()) / 86400000);
  };

  const getRemainingLabel = (endDate?: string) => {
    const left = daysBetween(new Date().toISOString(), endDate);
    if (left === null) return "—";
    if (left > 0) return `${left.toLocaleString()} Days Left`;
    if (left === 0) return "Last day";
    return "Expired";
  };

  const getValidityLabel = (startDate?: string, endDate?: string) => {
    const total = daysBetween(startDate, endDate);
    if (total === null || total < 0) return "—";
    return `${total.toLocaleString()} Days`;
  };

  // ============= Render =============

  return (
    <div>
      <div className="ab-filter-panel" style={{ padding: "20px 24px", background: "#fff", borderRadius: "8px", marginBottom: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
        <div className="ab-filter-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div>
            <h5 className="ab-filter-title" style={{ fontSize: "18px", fontWeight: "600", color: "#1e293b", margin: 0 }}>End-User Subscriptions</h5>
            <p className="ab-filter-subtitle" style={{ fontSize: "13px", color: "#64748b", margin: "4px 0 0 0" }}>
              Search and manage user subscriptions
            </p>
          </div>
        </div>

        <div className="ab-divider" style={{ borderTop: "1px solid #e2e8f0", margin: "12px 0 16px 0" }} />

        {/* Improved Filter UI */}
        <Row className="g-3 align-items-end">
          {/* Search Box - Full Width on Mobile, Takes remaining space */}
          <Col xs={12} md={6} lg={5}>
            <div style={{ position: "relative" }}>
              <label className="ab-field-label" style={{ fontSize: "13px", fontWeight: "500", color: "#475569", marginBottom: "4px", display: "block" }}>
                Search
              </label>
              <div style={{ position: "relative" }}>
                <FontAwesomeIcon 
                  icon={faSearch} 
                  style={{ 
                    position: "absolute", 
                    left: "12px", 
                    top: "50%", 
                    transform: "translateY(-50%)",
                    color: "#94a3b8",
                    fontSize: "14px"
                  }} 
                />
                <Form.Control
                  type="text"
                  placeholder="Search by name, mobile, email..."
                  value={searchText}
                  onChange={handleSearchInputChange}
                  style={{
                    height: "42px",
                    paddingLeft: "36px",
                    fontSize: "14px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    transition: "all 0.2s",
                    background: "#f8fafc"
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#3b82f6";
                    e.target.style.background = "#ffffff";
                    e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e2e8f0";
                    e.target.style.background = "#f8fafc";
                    e.target.style.boxShadow = "none";
                  }}
                />
                {searchText && (
                  <button
                    onClick={() => {
                      setSearchText("");
                      setDebouncedSearchText("");
                    }}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      color: "#94a3b8",
                      cursor: "pointer",
                      padding: "4px"
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} size="sm" />
                  </button>
                )}
              </div>
            </div>
          </Col>

          {/* From Date */}
          <Col xs={6} md={3} lg={2}>
            <div>
              <label className="ab-field-label" style={{ fontSize: "13px", fontWeight: "500", color: "#475569", marginBottom: "4px", display: "block" }}>
                <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: "6px", fontSize: "12px" }} />
                From Date
              </label>
              <Form.Control
                type="date"
                value={postedFromDate}
                onChange={handleFromDateChange}
                style={{
                  height: "42px",
                  fontSize: "14px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  padding: "0 12px",
                  background: "#f8fafc",
                  transition: "all 0.2s",
                  cursor: "pointer"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.background = "#ffffff";
                  e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2e8f0";
                  e.target.style.background = "#f8fafc";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </Col>

          {/* To Date */}
          <Col xs={6} md={3} lg={2}>
            <div>
              <label className="ab-field-label" style={{ fontSize: "13px", fontWeight: "500", color: "#475569", marginBottom: "4px", display: "block" }}>
                <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: "6px", fontSize: "12px" }} />
                To Date
              </label>
              <Form.Control
                type="date"
                value={postedToDate}
                onChange={handleToDateChange}
                style={{
                  height: "42px",
                  fontSize: "14px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  padding: "0 12px",
                  background: "#f8fafc",
                  transition: "all 0.2s",
                  cursor: "pointer"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.background = "#ffffff";
                  e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2e8f0";
                  e.target.style.background = "#f8fafc";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </Col>

          {/* Action Buttons */}
          <Col xs={12} md={12} lg={3}>
            <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
              <Button
                variant="primary"
                onClick={fetchSubscriptionsData}
                style={{
                  height: "42px",
                  fontSize: "14px",
                  fontWeight: "500",
                  padding: "0 24px",
                  borderRadius: "8px",
                  background: "#3b82f6",
                  border: "none",
                  color: "#fff",
                  flex: "1 1 auto",
                  minWidth: "100px"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#2563eb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#3b82f6";
                }}
              >
                <FontAwesomeIcon icon={faSearch} style={{ marginRight: "8px" }} />
                Apply Filters
              </Button>
              
              <Button
                variant="outline-secondary"
                onClick={clearFilters}
                style={{
                  height: "42px",
                  fontSize: "14px",
                  fontWeight: "500",
                  padding: "0 20px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  color: "#64748b",
                  background: "#fff",
                  flex: "1 1 auto",
                  minWidth: "80px"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f1f5f9";
                  e.currentTarget.style.borderColor = "#cbd5e1";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#fff";
                  e.currentTarget.style.borderColor = "#e2e8f0";
                }}
              >
                <FontAwesomeIcon icon={faTimes} style={{ marginRight: "6px" }} />
                Clear
              </Button>
            </div>
          </Col>
        </Row>

        {/* Active Filters Display */}
        {(searchText || postedFromDate || postedToDate) && (
          <div style={{ marginTop: "16px", display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: "13px", color: "#64748b", fontWeight: "500" }}>Active Filters:</span>
            {searchText && (
              <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 12px",
                background: "#eff6ff",
                color: "#3b82f6",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500"
              }}>
                Search: {searchText}
                <button
                  onClick={() => {
                    setSearchText("");
                    setDebouncedSearchText("");
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#3b82f6",
                    cursor: "pointer",
                    padding: "0",
                    display: "inline-flex"
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} size="xs" />
                </button>
              </span>
            )}
            {postedFromDate && (
              <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 12px",
                background: "#f0fdf4",
                color: "#16a34a",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500"
              }}>
                From: {formatDate(postedFromDate)}
                <button
                  onClick={() => {
                    setPostedFromDate("");
                    setCurrentPage(1);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#16a34a",
                    cursor: "pointer",
                    padding: "0",
                    display: "inline-flex"
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} size="xs" />
                </button>
              </span>
            )}
            {postedToDate && (
              <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 12px",
                background: "#f0fdf4",
                color: "#16a34a",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500"
              }}>
                To: {formatDate(postedToDate)}
                <button
                  onClick={() => {
                    setPostedToDate("");
                    setCurrentPage(1);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#16a34a",
                    cursor: "pointer",
                    padding: "0",
                    display: "inline-flex"
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} size="xs" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      <Card className="p-3">
        <AdvanceTableProvider {...table}>
          <div className="px-4 py-2 px-lg-6 bg-body-emphasis rounded position-relative top-1">
            {isLoading ? (
              <LoadingAnimation />
            ) : (
              <>
                <AdvanceTable
                  tableProps={{ className: "phoenix-table fs-9 p-0" }}
                  dataLength={tableData?.length}
                />
                <CustomAdvanceTableFooter
                  pagination
                  totalPages={totalPages}
                  totalRecords={totalRecords}
                  recordsPerPage={limit}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  CanPreviousPage={getCanPreviousPage}
                  CanNextPage={getCanNextPage}
                  handleNextPage={handleNext}
                  handlePreviousPage={handlePrevious}
                  setIsAllVisibleData={setIsAllVisibleData}
                  isAllVisibleData={isAllVisibleData}
                />
              </>
            )}
          </div>
        </AdvanceTableProvider>
      </Card>

      {/* ============ View Subscription Modal ============ */}
      <Modal
        show={showViewModal}
        onHide={handleCloseViewModal}
        centered
        dialogClassName="ab-sub-dialog"
        contentClassName="ab-sub-content"
        aria-labelledby="ab-sub-header-title"
      >
        <div className="ab-sub-header">
          <h5 className="ab-sub-header-title" id="ab-sub-header-title">
            Subscription Details
          </h5>
          <button
            type="button"
            className="ab-sub-close"
            onClick={handleCloseViewModal}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="ab-sub-body">
          {isLoadingDetail ? (
            <div className="ab-sub-state">
              <LoadingAnimation />
            </div>
          ) : selectedSubscription ? (
            <>
              {/* Hero summary */}
              <div className="ab-sub-hero">
                <div className="ab-sub-hero-top">
                  <div className="ab-sub-hero-badges">
                    {selectedSubscription.plan_name && (
                      <span className={`ab-pill ab-plan-${getPlanTier(selectedSubscription.plan_name)}`}>
                        <span className="ab-pill-dot" />
                        {selectedSubscription.plan_name}
                      </span>
                    )}
                    <span className={`ab-pill ab-status-${getStatusKey(selectedSubscription.status)}`}>
                      <span className="ab-pill-dot" />
                      {selectedSubscription.status || "N/A"}
                    </span>
                    {selectedSubscription.payment_status && (
                      <span className={`ab-pill ab-status-${selectedSubscription.payment_status === 'paid' ? 'success' : 'warning'}`}>
                        <span className="ab-pill-dot" />
                        {selectedSubscription.payment_status}
                      </span>
                    )}
                  </div>
                  <span className="ab-sub-hero-id">
                    {formatSubscriptionId(selectedSubscription.owner_subscription_id)}
                  </span>
                </div>

                <h2 className="ab-sub-plan-name">
                  {selectedSubscription.plan_name || "Subscription"}
                </h2>
                <span className="ab-sub-hero-range">
                  <FontAwesomeIcon icon={faCalendarDays} />
                  {safeFormatDate(selectedSubscription.start_date)}
                  {"  →  "}
                  {safeFormatDate(selectedSubscription.end_date)}
                </span>

                <div className="ab-sub-hero-stats">
                  <div className="ab-sub-stat">
                    <span className="ab-sub-stat-label">
                      <FontAwesomeIcon icon={faIndianRupeeSign} />
                      Amount
                    </span>
                    <span className="ab-sub-stat-value is-amount">
                      ₹{selectedSubscription.amount ? Number(selectedSubscription.amount).toLocaleString() : "0"}
                    </span>
                  </div>
                  <div className="ab-sub-stat">
                    <span className="ab-sub-stat-label">
                      <FontAwesomeIcon icon={faHourglassHalf} />
                      Validity
                    </span>
                    <span className="ab-sub-stat-value">
                      {getValidityLabel(selectedSubscription.start_date, selectedSubscription.end_date)}
                    </span>
                  </div>
                  <div className="ab-sub-stat">
                    <span className="ab-sub-stat-label">
                      <FontAwesomeIcon icon={faClock} />
                      Remaining
                    </span>
                    <span className="ab-sub-stat-value">
                      {getRemainingLabel(selectedSubscription.end_date)}
                    </span>
                  </div>
                  <div className="ab-sub-stat">
                    <span className="ab-sub-stat-label">
                      <FontAwesomeIcon icon={faCalendarDays} />
                      Created
                    </span>
                    <span className="ab-sub-stat-value">
                      {safeFormatDate(selectedSubscription.created_at)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Information cards */}
              <div className="ab-sub-cards">
                {/* Customer */}
                <div className="ab-sub-card">
                  <h6 className="ab-sub-card-title">
                    <FontAwesomeIcon icon={faUser} />
                    Customer
                  </h6>
                  <div className="ab-sub-info-list">
                    <div className="ab-sub-info-row">
                      <span className="ab-sub-info-icon">
                        <FontAwesomeIcon icon={faUser} />
                      </span>
                      <div className="ab-sub-info-text">
                        <span className="ab-sub-label">User Name</span>
                        <span className="ab-sub-value">
                          {selectedSubscription.name || "N/A"}
                        </span>
                      </div>
                    </div>
                    <div className="ab-sub-info-row">
                      <span className="ab-sub-info-icon">
                        <FontAwesomeIcon icon={faPhone} />
                      </span>
                      <div className="ab-sub-info-text">
                        <span className="ab-sub-label">Mobile</span>
                        <span className="ab-sub-value">
                          {selectedSubscription.mobile_number || "N/A"}
                        </span>
                      </div>
                    </div>
                    {selectedSubscription.email && (
                      <div className="ab-sub-info-row">
                        <span className="ab-sub-info-icon">
                          <FontAwesomeIcon icon={faEnvelope} />
                        </span>
                        <div className="ab-sub-info-text">
                          <span className="ab-sub-label">Email</span>
                          <span className="ab-sub-value">
                            {selectedSubscription.email}
                          </span>
                        </div>
                      </div>
                    )}
                    {selectedSubscription.role_name && (
                      <div className="ab-sub-info-row">
                        <span className="ab-sub-info-icon">
                          <FontAwesomeIcon icon={faBuilding} />
                        </span>
                        <div className="ab-sub-info-text">
                          <span className="ab-sub-label">Role</span>
                          <span className="ab-sub-value">
                            {selectedSubscription.role_name}
                          </span>
                        </div>
                      </div>
                    )}
                    {selectedSubscription.company_name && (
                      <div className="ab-sub-info-row">
                        <span className="ab-sub-info-icon">
                          <FontAwesomeIcon icon={faBuilding} />
                        </span>
                        <div className="ab-sub-info-text">
                          <span className="ab-sub-label">Company</span>
                          <span className="ab-sub-value">
                            {selectedSubscription.company_name}
                          </span>
                        </div>
                      </div>
                    )}
                    {selectedSubscription.company_address && (
                      <div className="ab-sub-info-row">
                        <span className="ab-sub-info-icon">
                          <FontAwesomeIcon icon={faLocationDot} />
                        </span>
                        <div className="ab-sub-info-text">
                          <span className="ab-sub-label">Address</span>
                          <span className="ab-sub-value">
                            {selectedSubscription.company_address}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Subscription */}
                <div className="ab-sub-card">
                  <h6 className="ab-sub-card-title">
                    <FontAwesomeIcon icon={faCalendarDays} />
                    Subscription
                  </h6>
                  <div className="ab-sub-kv">
                    <div className="ab-sub-kv-row">
                      <span className="ab-sub-label">Sub ID</span>
                      <span className="ab-sub-value">
                        {formatSubscriptionId(selectedSubscription.owner_subscription_id)}
                      </span>
                    </div>
                    <div className="ab-sub-kv-row">
                      <span className="ab-sub-label">Plan Count</span>
                      <span className="ab-sub-value">
                        {selectedSubscription.plan_count || 0}
                      </span>
                    </div>
                    <div className="ab-sub-kv-row">
                      <span className="ab-sub-label">Used Count</span>
                      <span className="ab-sub-value">
                        {selectedSubscription.used_count || 0}
                      </span>
                    </div>
                    <div className="ab-sub-kv-row">
                      <span className="ab-sub-label">Payment Status</span>
                      <span className="ab-sub-value">
                        {selectedSubscription.payment_status || "N/A"}
                      </span>
                    </div>
                    <div className="ab-sub-kv-row">
                      <span className="ab-sub-label">Created Date</span>
                      <span className="ab-sub-value">
                        {safeFormatDate(selectedSubscription.created_at)}
                      </span>
                    </div>
                    {selectedSubscription.updated_at && (
                      <div className="ab-sub-kv-row">
                        <span className="ab-sub-label">Updated Date</span>
                        <span className="ab-sub-value">
                          {safeFormatDate(selectedSubscription.updated_at)}
                        </span>
                      </div>
                    )}
                    {selectedSubscription.gst_number && (
                      <div className="ab-sub-kv-row">
                        <span className="ab-sub-label">GST Number</span>
                        <span className="ab-sub-value">
                          {selectedSubscription.gst_number}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Company Description */}
              {selectedSubscription.company_description && (
                <div className="ab-sub-desc-card">
                  <h6 className="ab-sub-card-title">
                    <FontAwesomeIcon icon={faBuilding} />
                    Company Description
                  </h6>
                  <div className="ab-sub-desc">
                    {selectedSubscription.company_description}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="ab-sub-state ab-sub-state--error">
              Failed to load subscription details
            </div>
          )}
        </div>

        <div className="ab-sub-footer">
          <button
            type="button"
            className="ab-sub-btn ab-sub-btn-primary"
            onClick={handleCloseViewModal}
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default UserSubscription;